import { grScope } from "@gr-common/async/logger";
import type { NexusHandler } from "../../nexus/function/function.types";
import type { CoreRaw } from "..";
import type { RunWithOptionsBase } from "./promise.types";

const log = grScope.scope("core.promise");

type ExecutorParameters<R> = Parameters<ConstructorParameters<typeof Promise<R>>[0]>;

const promise = <R>(fn: (...args: ExecutorParameters<R>) => unknown): Promise<R> =>
  new Promise<R>((...args) => fn(...args));

const tryRun = async <A extends unknown[], R>(
  fn: (...args: A) => R,
  resolvers: ExecutorParameters<R>,
  fnArgs: A,
  options: RunWithOptionsBase,
): Promise<void> => {
  try {
    if (options.signal?.aborted) {
      throw options.signal.reason;
    }

    resolvers[0](await fn(...fnArgs));
  } catch (e) {
    if (options.silent) {
      log.info("callback failed with", e);
      resolvers[0](undefined as never);
    }

    if (options.nofail) {
      resolvers[0](e as never);
    }

    resolvers[1](e);
  }
};

/**
 *
 */
export const runWithMicrotask: NexusHandler<CoreRaw["runWithMicrotask"]> = (
  fn,
  options = {},
  ...fnArgs
) =>
  promise((...resolvers) => queueMicrotask(() => tryRun(fn as never, resolvers, fnArgs, options)));

/**
 *
 */
export const runWithMacrotask: NexusHandler<CoreRaw["runWithMacrotask"]> = (
  fn,
  options = {},
  ...fnArgs
) =>
  promise((...resolvers) => {
    setTimeout(() => tryRun(fn as never, resolvers, fnArgs, options), options.timeout);
  });

export const runWithAnimationFrame: NexusHandler<CoreRaw["runWithAnimationFrame"]> = (
  fn,
  options = {},
  ...fnArgs
) =>
  promise((...resolvers) => {
    requestAnimationFrame((time) => tryRun(fn.bind(time) as never, resolvers, fnArgs, options));
  });

export const runWithRenderSlice: NexusHandler<CoreRaw["runWithRenderSlice"]> = (
  fn,
  options = {},
  ...fnArgs
) =>
  promise((...resolvers) => {
    requestAnimationFrame((time) => {
      setTimeout(() => tryRun(fn.bind(time), resolvers, fnArgs, options), options.timeout);
    });
  });
